import { Widget } from "@foretagsplatsen/widgetjs";
import regionWidget from "../../../core/widgets/regionWidget.js";

/**
 * Editor for the variation of percentage value.
 *
 * The variation of the value is displayed in the percentage scale as a progress
 * bar.
 *
 * @param {number} [spec.value] - Initial value.
 * @param {string} [spec.label] - Label displayed in the editor to represent the
 * value.
 * @param {string} [spec.barColor] - Color string used to fill the bar. Should be
 * a valid CSS color description.
 * @param {number} [spec.variationScale] - Absolute scale variation value in
 * percentage used for the progress bar.
 * @param {function} [spec.onChange] - Callback used on user interaction when the
 * value is changed. The function is called with the new value as argument.
 * @param {string} [name] - Used as "name" property for the input field
 */
export default class PercentageVariationEditorWidget extends Widget {
	constructor({
		value = 0,
		label = "",
		barColor = "#000000",
		variationScale = 15,
		onChange = () => {},
		name,
	} = {}) {
		super(...arguments);

		this._value = value;
		this._onChange = onChange;
		this._barColor = barColor;
		this._label = label;
		this._variationScale = variationScale;
		this._name = name;

		this._percentageBar = regionWidget({
			content: this._renderPercentageBarOn.bind(this),
		});
	}

	renderContentOn(html) {
		html.div(
			{ klass: "percentage-variation-editor" },
			this._renderControlGroupOn.bind(this),
			this._percentageBar,
		);
	}

	_renderControlGroupOn(html) {
		html.div(
			{ klass: "control-group" },
			html.span({ klass: "variation-label" }, this._label),
			this._renderInputOn.bind(this),
			"%",
		);
	}

	_renderPercentageBarOn(html) {
		const isNegative = this._value < 0;

		html.div(
			{ klass: "bar-container" },
			html.div(
				{ klass: "delimiters" },
				html.div({ klass: "delimiter minus" }),
				html.div({ klass: "delimiter zero" }),
				html.div({ klass: "delimiter plus" }),
			),
			html.div({
				klass: ["bar", { negative: isNegative, positive: !isNegative }],
				style: `width: ${this._computeBarWidth()}%; background-color: ${
					this._barColor
				}`,
			}),
		);
	}

	_renderInputOn(html) {
		html.input({
			type: "number",
			name: this._name,
			value: this._value,
			keyup: this._setValueFromInputEvent.bind(this),
			change: this._setValueFromInputEvent.bind(this),
		});
	}

	_computeBarWidth() {
		return Math.min(
			(Math.abs(this._value) / this._variationScale) * 50,
			50,
		);
	}

	_setValueFromInputEvent(event) {
		const input = event.target;
		this._value = parseInt(input.value, 10) || 0;
		this._onChange(this._value);
		this._percentageBar.update();
	}
}
